import React, { useState, useEffect, FC } from "react";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import { useNavigate } from "react-router";
import { formatCurrency } from "../../utils/Utils";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";
import {
  mockWorkExecutionData,
  mockWorkExecutionWidgetData,
  mockWorkExecutionStatusOptions,
  mockContractorOptions,
  mockContractTypeOptions,
  mockSiteOptions,
  mockWorkOrderOwnerOptions,
} from "./mockData/workExecutionData";
import { WorkExecutionData, WorkExecutionStatus } from "./types";
import WorkExecutionAside from "./workExecutionAside";
import {
  Drawer,
  Button,
  Badge,
  Select,
  Input,
  DatePicker,
  Form,
  Row,
  Col,
  Space,
  Divider,
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterState {
  workOrderNumber?: string;
  workOrderTitle?: string;
  siteId?: string[];
  contractorName?: string[];
  contractType?: string[];
  completionDateRange?: [any, any] | null;
  closureDateRange?: [any, any] | null;
  totalValueRange?: { min?: number; max?: number };
  executedValueRange?: { min?: number; max?: number };
  workOrderOwner?: string;
}

const ClosedWorks: FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [widgetData, setWidgetData] = useState<any>({});
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [workOrderOwnerOptions, setWorkOrderOwnerOptions] = useState<any>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [form] = Form.useForm();

  // KPI Filter Categories
  const kpiFilterOptions = [
    { label: "By Status", value: "status" },
    { label: "By Value", value: "value" },
    { label: "By Time", value: "time" },
  ];

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "object" && value !== null) {
          if ("min" in value && value.min !== undefined) count++;
          if ("max" in value && value.max !== undefined) count++;
        } else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  const getStatusColor = (
    status: WorkExecutionStatus
  ): { color: string; bgColor: string } => {
    switch (status) {
      case WorkExecutionStatus.Completed:
        return { color: "#198754", bgColor: "#D4EDDA" };
      case WorkExecutionStatus.Closed:
        return { color: "#495057", bgColor: "#E9ECEF" };
      case WorkExecutionStatus.Cancelled:
        return { color: "#DC3545", bgColor: "#F8D7DA" };
      default:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
    }
  };

  // Dynamic Widget Data Generator for closed works
  const getWidgetsByFilter = (filterType: string) => {
    // Calculate metrics for closed works only
    const closedWorks = mockWorkExecutionData.filter((item) =>
      [WorkExecutionStatus.Completed, WorkExecutionStatus.Closed].includes(
        item.status
      )
    );

    const totalClosedValue = closedWorks.reduce(
      (sum, item) => sum + item.totalValue,
      0
    );
    const totalExecutedValue = closedWorks.reduce(
      (sum, item) => sum + item.executedValue,
      0
    );
    const avgCompletionTime =
      closedWorks.length > 0
        ? closedWorks.reduce((sum, item) => {
            if (item.actualEndDate && item.actualStartDate) {
              const start = new Date(item.actualStartDate);
              const end = new Date(item.actualEndDate);
              return (
                sum +
                Math.floor(
                  (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
                )
              );
            }
            return sum;
          }, 0) / closedWorks.length
        : 0;

    switch (filterType) {
      case "status":
        return [
          {
            title: "Total Closed",
            key: "totalClosed",
            color: "#495057",
            percentage: "100",
            value: `${closedWorks.length}`,
            icon: "📊",
          },
          {
            title: "Completed",
            key: "completed",
            color: "#198754",
            percentage: `${
              (
                (closedWorks.filter(
                  (w) => w.status === WorkExecutionStatus.Completed
                ).length /
                  closedWorks.length) *
                100
              ).toFixed(1) || 0
            }`,
            value: `${
              closedWorks.filter(
                (w) => w.status === WorkExecutionStatus.Completed
              ).length
            }`,
            icon: "✅",
          },
          {
            title: "Closed",
            key: "closed",
            color: "#495057",
            percentage: `${
              (
                (closedWorks.filter(
                  (w) => w.status === WorkExecutionStatus.Closed
                ).length /
                  closedWorks.length) *
                100
              ).toFixed(1) || 0
            }`,
            value: `${
              closedWorks.filter((w) => w.status === WorkExecutionStatus.Closed)
                .length
            }`,
            icon: "🔒",
          },
        ];

      case "value":
        return [
          {
            title: "Total Closed Value",
            key: "totalClosedValue",
            color: "#495057",
            percentage: "100",
            value: formatCurrency(totalClosedValue, "INR"),
            icon: "💰",
          },
          {
            title: "Total Executed",
            key: "totalExecuted",
            color: "#198754",
            percentage: `${
              ((totalExecutedValue / totalClosedValue) * 100).toFixed(1) || 0
            }`,
            value: formatCurrency(totalExecutedValue, "INR"),
            icon: "💸",
          },
          {
            title: "Avg Work Value",
            key: "avgWorkValue",
            color: "#007BFF",
            percentage: "Per Work",
            value: formatCurrency(
              closedWorks.length > 0
                ? totalClosedValue / closedWorks.length
                : 0,
              "INR"
            ),
            icon: "📊",
          },
        ];

      case "time":
        return [
          {
            title: "Avg Completion Time",
            key: "avgCompletionTime",
            color: "#17A2B8",
            percentage: "Days",
            value: `${Math.round(avgCompletionTime)}`,
            icon: "⏱️",
          },
          {
            title: "This Month Closed",
            key: "thisMonthClosed",
            color: "#28A745",
            percentage: "Current Month",
            value: `${
              closedWorks.filter((w) => {
                if (w.actualEndDate) {
                  const endDate = new Date(w.actualEndDate);
                  const now = new Date();
                  return (
                    endDate.getMonth() === now.getMonth() &&
                    endDate.getFullYear() === now.getFullYear()
                  );
                }
                return false;
              }).length
            }`,
            icon: "📅",
          },
        ];

      default:
        return [];
    }
  };

  const calculateAge = (releasedDate: string) => {
    const today = new Date();
    const released = new Date(releasedDate);
    const diffTime = today.getTime() - released.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const columns = [
    {
      title: "Batch #",
      dataIndex: "workBatchSequenceId",
      key: "workBatchSequenceId",
       fixed: "left",
      width: 180,
      render: (text: string, record: any) => (
        <div>
          <Link
            to={`/work-execution-batch-details/${record.workExeBatchId}`}
            className="fw-bold text-primary"
          >
            {text}
          </Link>
          {/* <div className="text-muted small mt-1">Section: {record.section}</div> */}
        </div>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 180,
      // fixed: "left",
      // render(text: any, record: any) {
      //   return (
      //     <>
      //       <span
      //         className="inlineTableCellLink"
      //         id={`woExecution-${record?.id}`}
      //         onClick={() => {
      //           navigate(`/work-execution-view/${record.id}`);
      //         }}
      //       >
      //         {text}
      //       </span>
      //     </>
      //   );
      // },
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 200,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 40)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 180,
      render: (text: any, record: any) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName || "N/A"}</div>
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 150,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`contractor-tooltip-${index}`}>
              {text?.length > 20 ? `${text?.substring(0, 17)}... ` : text}
            </span>
            {text?.length > 20 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`contractor-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Closed On",
      dataIndex: "releasedOn",
      key: "releasedOn",
      width: 120,
      render: (date: any) => <span>{date ? FormatDate(date) : "---"}</span>,
    },
    {
      title: "Progress %",
      key: "progressPercentage",
      width: 100,
      render: (text: any, record: any) => {
        const progress = record.progressPercentage;
        return (
          <div style={{ textAlign: "center" }}>
            <span className="badge bg-success">{progress}%</span>
          </div>
        );
      },
    },
    {
      title: "Last Exec Update",
      dataIndex: "lastExecutionUpdate",
      key: "lastExecutionUpdate",
      width: 140,
      render: (date: any) => <span>{date ? FormatDate(date) : "---"}</span>,
    },
    // {
    //   title: "Age (Days)",
    //   key: "age",
    //   width: 100,
    //   render: (text: any, record: any) => {
    //     const age = record.releasedOn ? calculateAge(record.releasedOn) : 0;
    //     return (
    //       <div style={{ textAlign: "center" }}>
    //         <span
    //           className={`badge ${
    //             age > 30
    //               ? "bg-danger"
    //               : age > 15
    //               ? "bg-warning"
    //               : age > 7
    //               ? "bg-info"
    //               : "bg-success"
    //           }`}
    //         >
    //           {age}
    //         </span>
    //       </div>
    //     );
    //   },
    // },
  ];

  // Filter Functions
  const handleFilterDrawerOpen = () => {
    setTempFilters({ ...filters });
    form.setFieldsValue(filters);
    setFilterDrawerVisible(true);
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    form.resetFields();
  };

  const handleApplyFilters = () => {
    form
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          workOrderNumber: values.workOrderNumber?.trim() || undefined,
          workOrderTitle: values.workOrderTitle?.trim() || undefined,
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          contractType:
            values.contractType && values.contractType.length > 0
              ? values.contractType
              : undefined,
          completionDateRange: values.completionDateRange || undefined,
          closureDateRange: values.closureDateRange || undefined,
          totalValueRange: {
            min: values.totalValueMin || undefined,
            max: values.totalValueMax || undefined,
          },
          executedValueRange: {
            min: values.executedValueMin || undefined,
            max: values.executedValueMax || undefined,
          },
          workOrderOwner: values.workOrderOwner?.trim() || undefined,
        };

        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        if (
          processedFilters.totalValueRange &&
          !processedFilters.totalValueRange.min &&
          !processedFilters.totalValueRange.max
        ) {
          delete processedFilters.totalValueRange;
        }

        if (
          processedFilters.executedValueRange &&
          !processedFilters.executedValueRange.min &&
          !processedFilters.executedValueRange.max
        ) {
          delete processedFilters.executedValueRange;
        }

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    form.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      form.setFieldsValue(savedFilter);
    }
  };

  useEffect(() => {
    // Load mock data
    setWidgetData(mockWorkExecutionWidgetData);
    setContractorOptions(mockContractorOptions);
    setContractTypeOptions(mockContractTypeOptions);
    setSiteOptions(mockSiteOptions);
    setWorkOrderOwnerOptions(mockWorkOrderOwnerOptions);
  }, []);

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // KPI Filter Change Handler
  const handleKPIFilterChange = (filterValue: string) => {
    setSelectedKPIFilter(filterValue);
  };

  // Filter data to show only closed works (Completed, Closed)
  const filteredData = mockWorkExecutionData.filter((item) =>
    [WorkExecutionStatus.Completed, WorkExecutionStatus.Closed].includes(
      item.status
    )
  );

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="closed-works"
        title="Closed Works"
        columns={columns}
        data={filteredData}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkExecutionAside updateSm={setSm} sm={sm} />
          </div>
        }
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        extra={{
          tableScrollX: 2000,
          dateRange: false,
          excelExport: true,
          dataFilterTootip: "Filtering based on Work completion date",
        }}
      />

      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Advanced Filters</span>
            <Space>
              <Badge count={getActiveFilterCount()} size="small">
                <Button size="small" type="text" icon={<FilterOutlined />} />
              </Badge>
            </Space>
          </div>
        }
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" initialValues={filters}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="workOrderNumber" label="Work Order Number">
                <Input
                  placeholder="Enter WO Number"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderTitle" label="Work Order Title">
                <Input
                  placeholder="Enter WO Title"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="siteId" label="Site">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractorName" label="Contractor">
                <Select
                  mode="multiple"
                  placeholder="Select Contractors"
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractType" label="Contract Type">
                <Select
                  mode="multiple"
                  placeholder="Select Contract Types"
                  options={contractTypeOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderOwner" label="Work Order Owner">
                <Input
                  placeholder="Enter Owner Name"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Date Ranges
              </Divider>
            </Col>

            <Col span={24}>
              <Form.Item
                name="completionDateRange"
                label="Completion Date Range"
              >
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="closureDateRange" label="Closure Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Value Ranges
              </Divider>
            </Col>

            <Col span={12}>
              <Form.Item name="totalValueMin" label="Min Total Value">
                <Input
                  type="number"
                  placeholder="Minimum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="totalValueMax" label="Max Total Value">
                <Input
                  type="number"
                  placeholder="Maximum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="executedValueMin" label="Min Executed Value">
                <Input
                  type="number"
                  placeholder="Minimum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="executedValueMax" label="Max Executed Value">
                <Input
                  type="number"
                  placeholder="Maximum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default ClosedWorks;
